import { useCallback, useEffect, useMemo, useState } from 'react';
import debounce from 'lodash/debounce';

import { OutcomeOption } from 'lib/form/tag-autocomplete/outcomes/tag-autocomplete-outcomes.constants';
import TagAutocompleteOutcomes from 'lib/form/tag-autocomplete/outcomes/tag-autocomplete-outcomes';
import { mapOutcomeToOption } from '../outcomes/outcome.utils';
import { TeachingLearningSequenceLabel } from './teaching-learning-sequence.style';
import { useLoggingLazyQuery } from 'lib/apollo/apollo-hooks';
import type {
  SearchOutcomesQuery,
  SearchOutcomesQueryVariables,
} from '../graphql/outcomes/outcomes.graphql.generated';
import { SEARCH_OUTCOMES } from '../graphql/outcomes/outcomes.graphql';
import {
  InquisitiveSubject,
  InquisitiveYears,
} from 'global/graphql/scalars.type';
import { Box } from 'approot/shared/design-system/box/box.style';
import { TeachingAndLearningOutcome } from './teaching-learning-sequence.constants';
import { addAndRemoveOptions } from './teaching-learning-standards.utils';

type Props = {
  label: string;
  subject: InquisitiveSubject;
  years: InquisitiveYears[];
  savedOutcomes: TeachingAndLearningOutcome[];
  onChange: (outcomes: TeachingAndLearningOutcome[]) => void;
};

const TeachingLearningStandards = ({
  label,
  savedOutcomes,
  subject,
  years,
  onChange,
}: Props) => {
  const [localOptions, setLocalOptions] = useState<OutcomeOption[]>(
    savedOutcomes.map(outcome => mapOutcomeToOption(outcome))
  );

  const [searchOutcomes, { data: outcomesData, loading }] = useLoggingLazyQuery<
    SearchOutcomesQuery,
    SearchOutcomesQueryVariables
  >(SEARCH_OUTCOMES);

  const fullOutcomeOptions = useMemo(() => {
    return (
      outcomesData?.searchOutcomes.map(outcome =>
        mapOutcomeToOption(outcome)
      ) || []
    );
  }, [outcomesData]);

  useEffect(() => {
    searchOutcomes({
      variables: {
        input: {
          subjects: [subject],
          years,
        },
      },
    });
    //eslint-disable-next-line
  }, []);

  const handleOnOptionsChange = useCallback(
    (values: OutcomeOption[]) => {
      if (outcomesData) {
        onChange(
          addAndRemoveOptions({
            updatedOutcomeList: values,
            savedOutcomes,
            fullOutcomeList: outcomesData.searchOutcomes,
          })
        );
      }
    },
    [savedOutcomes, outcomesData, onChange]
  );

  const debounceOnOptionsChange = useMemo(
    () => debounce(handleOnOptionsChange, 1000),
    [handleOnOptionsChange]
  );

  return (
    <Box $marginBottom={24}>
      <TeachingLearningSequenceLabel>{label}</TeachingLearningSequenceLabel>
      <TagAutocompleteOutcomes
        id={`${subject}-connections`}
        isMulti
        isLoading={loading}
        placeholder="Search"
        values={localOptions}
        options={fullOutcomeOptions}
        allOtherOptions={[]}
        optionsId={`${subject}-${years.join('_')}`}
        handleChange={(options: OutcomeOption[]) => {
          setLocalOptions(() => options);
          debounceOnOptionsChange(options);
        }}
      />
    </Box>
  );
};

export default TeachingLearningStandards;
